import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "";

const SOURCE_META = {
  lbnl_queued_up: {
    label: "LBNL Interconnection Queue",
    description: "36,441 projects across 80 ISO markets. Withdrawal rates compute queue delay scores.",
    color: "#185FA5",
    formula: "queue_delay = MIN(100, withdrawal_rate × 1.45)"
  },
  eia_860: {
    label: "EIA Form 860 — Grid Capacity",
    description: "Power plant capacity by state. Used to compute grid component of site risk.",
    color: "#1D9E75",
    formula: "grid_score = normalized capacity trend over 18-month window"
  },
  noaa_climate: {
    label: "NOAA Climate Data",
    description: "30-day rolling climate normals by state. Climate component of site risk.",
    color: "#BA7517",
    formula: "climate_score = composite of temperature, precipitation, severe weather risk"
  },
  bls_construction: {
    label: "BLS Construction Labor",
    description: "Bureau of Labor Statistics construction employment by state/trade.",
    color: "#8B5CF6",
    formula: "labor_score = normalized construction employment availability"
  },
  perf_gov: {
    label: "USASpending.gov Permits",
    description: "Federal permit backlog by state. Used in procurement risk calculation.",
    color: "#E24B4A",
    formula: "procurement_risk = (capacity_trend × 0.50) + (permit_backlog / 500 × 0.50)"
  },
  usace_permits: {
    label: "USACE Permits",
    description: "US Army Corps of Engineers permit data.",
    color: "#6B7280",
    formula: "Supplementary permit backlog signal"
  },
  interconnection_fyi: {
    label: "Interconnection.fyi",
    description: "Supplementary interconnection queue data.",
    color: "#6B7280",
    formula: "Supplementary queue signal"
  },
};

const fmtTime = (ts) => {
  if (!ts) return "\u2014";
  const d = new Date(ts);
  return d.toLocaleString();
};

const freshness = (ts) => {
  if (!ts) return { label: "Unknown", color: "#6B7280" };
  const hours = (Date.now() - new Date(ts)) / 3600000;
  if (hours < 25) return { label: "Fresh", color: "#1D9E75" };
  if (hours < 72) return { label: "Recent", color: "#BA7517" };
  return { label: "Stale", color: "#E24B4A" };
};

export default function IntelligenceDataSources() {
  const [sources, setSources] = useState([]);
  const [samples, setSamples] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/intelligence/sources`)
      .then(r => r.json())
      .then(d => { setSources(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const loadSample = async (key) => {
    if (samples[key]) return;
    const r = await fetch(`${API}/intelligence/sources/${key}/sample?limit=5`);
    const d = await r.json();
    setSamples(prev => ({ ...prev, [key]: d }));
  };

  const toggle = (key) => {
    if (expanded === key) {
      setExpanded(null);
    } else {
      setExpanded(key);
      loadSample(key);
    }
  };

  if (loading) return <div style={{ padding: 48, color: "#9a99a2" }}>Loading data sources...</div>;

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#e2e0d8",
                  fontFamily: "'Inter', sans-serif", padding: "24px 32px" }}>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "#5f5e6e", letterSpacing: 2,
                      textTransform: "uppercase", marginBottom: 4 }}>
          Intelligence Data
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#e8e6de" }}>
          Data Sources
        </h1>
        <div style={{ fontSize: 12, color: "#5f5e6e", marginTop: 4 }}>
          All public dataset sources feeding CORR-004 · click any source to inspect records
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {sources.map(src => {
          const meta = SOURCE_META[src.source_key] || { label: src.source_key, color: "#6B7280", description: "", formula: "" };
          const fresh = freshness(src.last_fetched);
          const isExpanded = expanded === src.source_key;
          const sampleData = samples[src.source_key] || [];

          return (
            <div key={src.source_key}
              onClick={() => toggle(src.source_key)}
              style={{
                background: isExpanded ? "#1a1a2e" : "#12121f",
                border: `1px solid ${isExpanded ? meta.color + "44" : "#2a2a3e"}`,
                borderLeft: `3px solid ${meta.color}`,
                borderRadius: 8, padding: "16px 20px", cursor: "pointer",
                transition: "border-color 0.2s",
              }}
            >
              {/* Header row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#e8e6de", marginBottom: 4 }}>
                    {meta.label}
                  </div>
                  <div style={{ fontSize: 12, color: "#5f5e6e", marginBottom: 8 }}>
                    {meta.description}
                  </div>
                  <div style={{
                    fontSize: 11, color: meta.color, fontFamily: "monospace",
                    background: meta.color + "11", padding: "3px 8px",
                    borderRadius: 4, display: "inline-block"
                  }}>
                    {meta.formula}
                  </div>
                </div>
                <div style={{ textAlign: "right", marginLeft: 24, flexShrink: 0 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: meta.color }}>
                    {src.record_count?.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: "#5f5e6e" }}>records</div>
                  <div style={{
                    marginTop: 6, fontSize: 10, color: fresh.color,
                    background: fresh.color + "22", padding: "2px 8px",
                    borderRadius: 10, display: "inline-block"
                  }}>
                    {fresh.label}
                  </div>
                  <div style={{ fontSize: 10, color: "#5f5e6e", marginTop: 4 }}>
                    {fmtTime(src.last_fetched)}
                  </div>
                </div>
              </div>

              {/* Expanded sample records */}
              {isExpanded && (
                <div style={{ marginTop: 16, borderTop: "1px solid #2a2a3e", paddingTop: 12 }}
                     onClick={e => e.stopPropagation()}>
                  <div style={{ fontSize: 11, color: "#5f5e6e", marginBottom: 8 }}>
                    Sample records (5 most recent)
                  </div>
                  {sampleData.length === 0 ? (
                    <div style={{ color: "#5f5e6e", fontSize: 12 }}>Loading samples...</div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <thead>
                          <tr style={{ color: "#5f5e6e" }}>
                            {["ID", "State", "ISO Region", "Date", "Value", "Category"].map(h => (
                              <th key={h} style={{ padding: "4px 8px", textAlign: "left",
                                                   borderBottom: "1px solid #2a2a3e" }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sampleData.map(row => (
                            <tr key={row.id} style={{ borderBottom: "1px solid #1a1a2e" }}>
                              <td style={{ padding: "4px 8px", color: "#5f5e6e" }}>{row.id}</td>
                              <td style={{ padding: "4px 8px" }}>{row.geo_state || "\u2014"}</td>
                              <td style={{ padding: "4px 8px" }}>{row.iso_region || "\u2014"}</td>
                              <td style={{ padding: "4px 8px" }}>{row.record_date || "\u2014"}</td>
                              <td style={{ padding: "4px 8px", color: meta.color }}>
                                {row.numeric_value != null ? Number(row.numeric_value).toFixed(2) : "\u2014"}
                              </td>
                              <td style={{ padding: "4px 8px", color: "#9a99a2" }}>{row.category || "\u2014"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
